import React from "react";
import { BlockNoteSchema, defaultBlockSpecs } from "@blocknote/core";
import { createReactBlockSpec } from "@blocknote/react";
import { Alert } from "./Alert";
import { Quiz } from "./Quiz";
import { Checkpoint } from "./Checkpoint";
import { PDF } from "./PDF";
import { Signature } from "./Signature";
import { DocumentUpload } from "./DocumentUpload";
import { ManagerInput } from "./ManagerInput";
import { SwipeDeck } from "./SwipeDeck";
import { Form } from "./Form";
import { Roleplay } from "./Roleplay";
import { VideoEmbed } from "./VideoEmbed";

// Single source of truth for the custom training block DEFINITIONS
// (propSchema / content; the block `type` derives from each config's key)
// shared by every BlockNote schema:
//   - SectionInputsModal2 (authoring editor)
//   - TrackEditorModal + ModuleEditorModal (read-only previews)
//   - take_training_employee/CompleteTrack (learner player)
//
// BlockNote strips props it doesn't recognize when loading a document, so a
// schema that lags the editor's silently de-configures blocks (worst case:
// content copied out of a lagging surface and pasted into the editor loses
// its drag-proof uuid mirrors AND its object props, saving a duplicate asset
// with orphaned completions). Centralizing the configs makes that class of
// drift impossible: every surface loads the same superset propSchema, and
// adding a prop is a one-file change.
//
// Render functions are deliberately NOT centralized for the editor/player —
// they close over component state (modal openers, refs, completion POSTs), so
// those two surfaces pass their own renders to buildTrainingSchema(). The two
// preview modals' renders were already identical, so they share
// buildPreviewSchema() below.
//
// Prop conventions (see the per-block comments):
//   - *Selected object props are wiped by BlockNote on drag/copy-paste; the
//     primitive *Uuid/*Name mirrors survive and the backend re-hydrates the
//     object on read.
//   - content: "inline" on interactive blocks so clicking places a cursor
//     instead of drawing a whole-block NodeSelection ring; "none" for atomic
//     media-style blocks. These MUST be uniform across surfaces — this file
//     is what enforces that. (The player's signature block previously said
//     "none"; unified to "inline" to match the editor and previews.)
export const TRAINING_BLOCK_CONFIGS = {
  quiz: {
    propSchema: {
      quizSelected: { type: "object", default: null },
      // Primitive mirror of quizSelected.uuid/name. BlockNote only serializes
      // primitive props, so the object `quizSelected` is wiped on
      // drag/copy-paste; these string props survive and let the block stay
      // configured (the backend re-hydrates the full object on read).
      quizUuid: { type: "string", default: "" },
      quizName: { type: "string", default: "" },
      quizzesData: { type: "array", required: true },
      homeCompany: { type: "string", required: true },
    },
    content: "inline",
  },
  checkpoint: {
    propSchema: {
      checkpointSelected: { type: "object", default: null },
      // Drag-proof mirror of the backing-record uuid (object props are wiped
      // on drag; without this the save would create a duplicate checkpoint
      // and orphan its completions). Backend stamps/reads it.
      checkpointUuid: { type: "string", default: "" },
      homeCompany: { type: "string", required: true },
      title: { type: "string", default: "Manager Approval Required" },
      save_to_profile: { type: "boolean", default: false },
    },
    content: "inline",
  },
  pdf: {
    propSchema: {
      name: { type: "string", default: "" },
      url: { type: "string", default: "" },
      showPreview: { type: "boolean", default: true },
    },
    content: "none",
  },
  documentUpload: {
    propSchema: {
      url: { type: "string" },
      file_uuid: { type: "string" },
      title: { type: "string", default: "Upload Document" },
      description: { type: "string", default: "" },
      required: { type: "boolean", default: true },
      requires_approval: { type: "boolean", default: false },
      save_to_profile: { type: "boolean", default: true },
      // Optional certificate-type mapping (slug). Primitive string =
      // drag-safe; empty = not mapped. Backend validates on save.
      certificateTypeSlug: { type: "string", default: "" },
      documentUploadBlockData: { type: "object" },
      // Drag-proof mirror of the backing-record uuid (prevents a duplicate
      // upload block + orphaned submissions on drag-then-save).
      documentUploadUuid: { type: "string", default: "" },
    },
    content: "inline",
  },
  signature: {
    propSchema: {
      // signatureMode: "acknowledgement" (default, no PDF) | "form" | "pdf" (legacy)
      signatureMode: { type: "string", default: "acknowledgement" },
      title: { type: "string", default: "" },
      // The statement the signer acknowledges (acknowledgement mode).
      acknowledgementText: { type: "string", default: "" },
      consentDisclosureVersion: { type: "string", default: "" },
      pdfName: { type: "string" },
      file_uuid: { type: "string" },
      fieldLocations: { type: "array" },
      // Drag-proof JSON mirror of fieldLocations (an array prop, which
      // BlockNote also wipes on drag — and it's NOT re-hydrated server-side,
      // so it would be lost permanently). Signature.js parses/self-heals it.
      fieldLocationsJson: { type: "string", default: "" },
      totalPages: { type: "number" },
      completed_pdf_uuid: { type: "string" },
      completed_at: { type: "string" },
      signatureBlockData: { type: "object" },
      saveToProfile: { type: "boolean", default: true },
      // Drag-proof mirror of the backing-record uuid (prevents a duplicate
      // signature block + orphaned signatures on drag-then-save).
      signatureUuid: { type: "string", default: "" },
    },
    content: "inline",
  },
  managerInput: {
    propSchema: {
      title: { type: "string", default: "Manager Input Required" },
      description: { type: "string", default: "" },
      required: { type: "boolean", default: true },
      save_to_profile: { type: "boolean", default: false },
      managerInputBlockData: { type: "object" },
      // Drag-proof mirror of the backing-record uuid (prevents a duplicate
      // manager-input block + orphaned responses on drag-then-save).
      managerInputUuid: { type: "string", default: "" },
    },
    content: "inline",
  },
  swipeDeck: {
    propSchema: {
      swipeDeckSelected: { type: "object", default: null },
      // Drag-proof primitive mirror of swipeDeckSelected.uuid/name (object
      // props are wiped on drag; strings survive). Backend re-hydrates the
      // full object (cards_count, completion) on read.
      swipeDeckUuid: { type: "string", default: "" },
      swipeDeckName: { type: "string", default: "" },
    },
    content: "none",
  },
  form: {
    propSchema: {
      formSelected: { type: "object", default: null },
      // Drag-proof primitive mirror of formSelected.uuid/name (object props
      // are wiped on drag; strings survive). Form.js resolves the rest.
      formUuid: { type: "string", default: "" },
      formName: { type: "string", default: "" },
    },
    // "inline" so clicking the block places a cursor instead of selecting
    // the whole block (a "none"/atomic block draws a NodeSelection ring).
    content: "inline",
  },
  roleplay: {
    propSchema: {
      roleplaySelected: { type: "object", default: null },
      // Drag-proof primitive mirror of roleplaySelected.uuid/name (object
      // props are wiped on drag; strings survive).
      roleplayUuid: { type: "string", default: "" },
      roleplayName: { type: "string", default: "" },
    },
    content: "inline",
  },
  videoEmbed: {
    propSchema: {
      // All primitives so they survive BlockNote drag/copy-paste (object
      // props get wiped). The block is self-contained — the URL is
      // reconstructed from provider+videoId at render, never stored raw.
      provider: { type: "string", default: "" },
      videoId: { type: "string", default: "" },
      hash: { type: "string", default: "" },
      startSeconds: { type: "number", default: 0 },
      title: { type: "string", default: "" },
    },
    content: "none",
  },
};

// Rendered in place of a block whose surface forgot to supply a render —
// degrades one block instead of white-screening the whole editor/player
// (there is no error boundary in the app and no FE test mounts these
// surfaces, so a thrown error here would first appear in front of a user).
const missingRenderFallback = (name) => () => (
  <div
    style={{
      border: "1px dashed #d97706",
      borderRadius: 8,
      padding: "8px 12px",
      color: "#92400e",
      fontSize: 13,
    }}
  >
    This content block ("{name}") can't be displayed here.
  </div>
);

// Builds the full training schema from the shared configs plus one render
// function per custom block. The block's `type` is derived from its config
// key so the schema map key and the ProseMirror node name can never diverge
// (BlockNote keys the schema by the map key but names nodes by config.type —
// a mismatch breaks node→block conversion at read time). A missing render
// logs loudly and renders a visible fallback block rather than throwing
// mid-render.
export function buildTrainingSchema(renders) {
  const customSpecs = {};
  for (const [name, config] of Object.entries(TRAINING_BLOCK_CONFIGS)) {
    let render = renders[name];
    if (typeof render !== "function") {
      console.error(
        `buildTrainingSchema: missing render for "${name}" block — rendering a fallback. ` +
          "Add a render for it at this call site (see trainingBlockSpecs.js)."
      );
      render = missingRenderFallback(name);
    }
    customSpecs[name] = createReactBlockSpec(
      { ...config, type: name },
      { render }
    );
  }
  return BlockNoteSchema.create({
    blockSpecs: {
      ...defaultBlockSpecs,
      alert: Alert,
      ...customSpecs,
    },
  });
}

// Read-only schema for the section previews inside TrackEditorModal and
// ModuleEditorModal (their renders were identical, so they share this).
export function buildPreviewSchema({ homeCompany, quizzesData, previewLanguage }) {
  return buildTrainingSchema({
    quiz: (innerProps) => (
      <Quiz
        {...innerProps}
        quizzesData={quizzesData}
        homeCompany={homeCompany}
        type="Preview"
      />
    ),
    checkpoint: (innerProps) => (
      <Checkpoint {...innerProps} mode="complete" isPreview={true} />
    ),
    pdf: (innerProps) => <PDF {...innerProps} />,
    signature: (innerProps) => (
      <Signature
        {...innerProps}
        homeCompany={homeCompany}
        mode="complete"
        isPreview={true}
      />
    ),
    documentUpload: (innerProps) => (
      <DocumentUpload
        {...innerProps}
        homeCompany={homeCompany}
        mode="complete"
        isPreview={true}
      />
    ),
    managerInput: (innerProps) => (
      <ManagerInput
        {...innerProps}
        homeCompany={homeCompany}
        mode="complete"
        isPreview={true}
      />
    ),
    swipeDeck: (innerProps) => (
      <SwipeDeck
        {...innerProps}
        homeCompany={homeCompany}
        mode="preview"
        language={previewLanguage}
      />
    ),
    form: (innerProps) => (
      <Form {...innerProps} homeCompany={homeCompany} mode="preview" />
    ),
    roleplay: (innerProps) => (
      <Roleplay {...innerProps} homeCompany={homeCompany} mode="preview" />
    ),
    videoEmbed: (innerProps) => <VideoEmbed {...innerProps} mode="preview" />,
  });
}
